import productBranding from './product-branding.data.json' with { type: 'json' };

export const DEFAULT_PRODUCT_BRAND_NAME = 'Terra Security';

export const DEFAULT_PRODUCT_LEGAL_ENTITY_NAME = 'Terra Security, Inc.';

export const PRODUCT_BRAND_NAME_TOKEN = '{brandName}';

export const DEFAULT_PRODUCT_APP_URL = 'https://app.terra.security';

export const DEFAULT_PRODUCT_API_URL = 'https://api.terra.security';

export const DEFAULT_PRODUCT_PUBLIC_WEBSITE = 'www.terra.security';

export const PRODUCT_APP_URL_TOKEN = '{appUrl}';

export const PRODUCT_API_URL_TOKEN = '{apiUrl}';

export const DEFAULT_PRODUCT_SUPPORT_EMAIL = 'support@terra.security';

export const PRODUCT_SUPPORT_EMAIL_TOKEN = '{supportEmail}';

export const DEFAULT_PRODUCT_SECURITY_EMAIL = 'security@terra.security';

export const PRODUCT_SECURITY_EMAIL_TOKEN = '{securityEmail}';

export const PRODUCT_PUBLIC_WEBSITE_TOKEN = '{publicWebsite}';

export const PRODUCT_DISPLAY_NAME = productBranding.displayName;

export const PRODUCT_EXECUTABLE_NAME = productBranding.executableName;

export const PRODUCT_AGENTIC_PROXY_LABEL = productBranding.agenticProxyLabel;

export const DOCS_CONTENT_AREA_PORTAL = productBranding.docsContentAreaPortal;

export const DOCS_CONTENT_AREA_GUIDES = productBranding.docsContentAreaGuides;

export const DOCS_SEARCH_AREA_GUIDES_LABEL =
  productBranding.docsSearchAreaGuidesLabel;

export function resolveProductBrandName({
  enabled,
  name,
}: {
  enabled?: string | undefined;
  name?: string | null;
}): string {
  const isEnabled = enabled === 'true';

  if (!isEnabled) {
    return DEFAULT_PRODUCT_BRAND_NAME;
  }

  return name?.trim() || '';
}

export function applyProductBrandName({
  text,
  brandName,
}: {
  text: string;
  brandName: string;
}): string {
  return text.replaceAll(PRODUCT_BRAND_NAME_TOKEN, brandName);
}

export function resolveProductPublicOrigin({
  brandingEnabled,
  url,
  fallback,
}: {
  brandingEnabled: boolean;
  url?: string | null;
  fallback: string;
}): string {
  if (!brandingEnabled) {
    return fallback;
  }

  const trimmed = url?.trim();

  if (!trimmed) {
    return '';
  }

  try {
    return new URL(trimmed).origin;
  } catch {
    return '';
  }
}

export function resolveProductPublicUrls({
  brandingEnabled,
  appUrl,
  apiUrl,
}: {
  brandingEnabled: boolean;
  appUrl?: string | null;
  apiUrl?: string | null;
}): { appUrl: string; apiUrl: string } {
  return {
    appUrl: resolveProductPublicOrigin({
      brandingEnabled,
      url: appUrl,
      fallback: DEFAULT_PRODUCT_APP_URL,
    }),
    apiUrl: resolveProductPublicOrigin({
      brandingEnabled,
      url: apiUrl,
      fallback: DEFAULT_PRODUCT_API_URL,
    }),
  };
}

export function applyProductPublicUrls({
  text,
  appUrl,
  apiUrl,
}: {
  text: string;
  appUrl: string;
  apiUrl: string;
}): string {
  return text
    .replaceAll(PRODUCT_APP_URL_TOKEN, appUrl)
    .replaceAll(PRODUCT_API_URL_TOKEN, apiUrl);
}

function resolveBrandedMailbox({
  brandingEnabled,
  email,
  fallback,
}: {
  brandingEnabled: boolean;
  email?: string | null;
  fallback: string;
}): string {
  if (!brandingEnabled) {
    return fallback;
  }

  return email?.trim() || '';
}

export function resolveProductSupportEmail({
  brandingEnabled,
  email,
}: {
  brandingEnabled: boolean;
  email?: string | null;
}): string {
  return resolveBrandedMailbox({
    brandingEnabled,
    email,
    fallback: DEFAULT_PRODUCT_SUPPORT_EMAIL,
  });
}

export function resolveProductSecurityEmail({
  brandingEnabled,
  email,
}: {
  brandingEnabled: boolean;
  email?: string | null;
}): string {
  return resolveBrandedMailbox({
    brandingEnabled,
    email,
    fallback: DEFAULT_PRODUCT_SECURITY_EMAIL,
  });
}

export function applyProductDocsPlaceholders({
  text,
  brandName,
  appUrl,
  apiUrl,
  supportEmail,
  securityEmail,
}: {
  text: string;
  brandName: string;
  appUrl: string;
  apiUrl: string;
  supportEmail: string;
  securityEmail: string;
}): string {
  return text
    .replaceAll(PRODUCT_BRAND_NAME_TOKEN, brandName)
    .replaceAll(PRODUCT_APP_URL_TOKEN, appUrl)
    .replaceAll(PRODUCT_API_URL_TOKEN, apiUrl)
    .replaceAll(PRODUCT_SUPPORT_EMAIL_TOKEN, supportEmail)
    .replaceAll(PRODUCT_SECURITY_EMAIL_TOKEN, securityEmail);
}

export function resolvePdfBrandLabel({
  vendorName,
  brandName,
  isWhiteLabeled,
  productFallback,
}: {
  vendorName?: string | null;
  brandName: string;
  isWhiteLabeled: boolean;
  productFallback: string;
}): string {
  const fromVendor = vendorName?.trim();

  if (fromVendor) {
    return fromVendor;
  }

  if (isWhiteLabeled) {
    return brandName.trim();
  }

  return productFallback;
}

export function resolvePdfAgenticProxyLabel({
  isWhiteLabeled,
  brandName,
}: {
  isWhiteLabeled: boolean;
  brandName: string;
}): string {
  if (!isWhiteLabeled) {
    return PRODUCT_AGENTIC_PROXY_LABEL;
  }

  const trimmed = brandName.trim();

  if (trimmed) {
    return `${trimmed} agentic Proxy (${PRODUCT_DISPLAY_NAME})`;
  }

  return `Agentic Proxy (${PRODUCT_DISPLAY_NAME})`;
}

export function resolvePdfPossessiveBrandLabel({
  brandLabel,
}: {
  brandLabel: string;
}): string {
  const trimmed = brandLabel.trim();

  if (!trimmed) {
    return '';
  }

  return trimmed.endsWith('s') ? `${trimmed}'` : `${trimmed}'s`;
}

export function resolveProductPublicWebsite({
  brandingEnabled,
  website,
}: {
  brandingEnabled: boolean;
  website?: string | null;
}): string {
  if (!brandingEnabled) {
    return DEFAULT_PRODUCT_PUBLIC_WEBSITE;
  }

  return website?.trim() || '';
}

export type PdfProductBranding = {
  brandLabel: string;
  legalEntityLabel: string;
  copyrightHolder: string;
  possessiveBrandLabel: string;
  agenticProxyLabel: string;
  website: string;
  supportEmail: string;
  isWhiteLabeled: boolean;
};

export function resolvePdfProductBranding({
  vendorName,
  brandName,
  isWhiteLabeled,
  supportEmail,
  website,
}: {
  vendorName?: string | null;
  brandName: string;
  isWhiteLabeled: boolean;
  supportEmail?: string | null;
  website?: string | null;
}): PdfProductBranding {
  const brandLabel = resolvePdfBrandLabel({
    vendorName,
    brandName,
    isWhiteLabeled,
    productFallback: DEFAULT_PRODUCT_BRAND_NAME,
  });

  const legalEntityLabel = resolvePdfBrandLabel({
    vendorName,
    brandName,
    isWhiteLabeled,
    productFallback: DEFAULT_PRODUCT_LEGAL_ENTITY_NAME,
  });

  const copyrightHolder = legalEntityLabel
    ? legalEntityLabel.endsWith('.')
      ? legalEntityLabel
      : `${legalEntityLabel}.`
    : '';

  return {
    brandLabel,
    legalEntityLabel,
    copyrightHolder,
    possessiveBrandLabel: resolvePdfPossessiveBrandLabel({ brandLabel }),
    agenticProxyLabel: resolvePdfAgenticProxyLabel({
      isWhiteLabeled,
      brandName: brandLabel || brandName,
    }),
    website: resolveProductPublicWebsite({
      brandingEnabled: isWhiteLabeled,
      website,
    }),
    supportEmail: resolveProductSupportEmail({
      brandingEnabled: isWhiteLabeled,
      email: supportEmail,
    }),
    isWhiteLabeled,
  };
}
